import type { DefaultRootState } from 'react-redux';

import { EMPTY_ARRAY } from 'utils/clickme';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';

export enum BalanceHistoryType {
    ActAdvertising = 'ACT_ADVERTISING',
    ActInformation = 'ACT_INFORMATION',
    ActInformationTop = 'ACT_INFORMATION_TOP',
    ActInformationMulti = 'ACT_INFORMATION_MULTI',
    ActUndefined = 'ACT_UNDEFINED',
    PaymentNew = 'PAYMENT_NEW',
    PaymentPayed = 'PAYMENT_PAYED',
    PaymentCancel = 'PAYMENT_CANCEL',
    Refund = 'REFUND',
    BonusSpend = 'BONUS_SPEND',
    BonusExpire = 'BONUS_EXPIRE',
    BonusRefill = 'BONUS_REFILL',
}

export enum BalanceHistoryFilterType {
    Operation = 'Operation',
    Bonus = 'Bonus',
}
export enum BalanceHistoryTransactionType {
    Profit = 'Profit',
    Expense = 'Expense',
    Flat = 'Flat',
}

export const BALANCE_HISTORY_CONFIG = {
    [BalanceHistoryType.ActAdvertising]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.ActInformation]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.ActInformationTop]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.ActInformationMulti]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.ActUndefined]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.Refund]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.PaymentNew]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Flat,
    },
    [BalanceHistoryType.PaymentPayed]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Profit,
    },
    [BalanceHistoryType.PaymentCancel]: {
        filterType: BalanceHistoryFilterType.Operation,
        transactionType: BalanceHistoryTransactionType.Flat,
    },
    [BalanceHistoryType.BonusSpend]: {
        filterType: BalanceHistoryFilterType.Bonus,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.BonusExpire]: {
        filterType: BalanceHistoryFilterType.Bonus,
        transactionType: BalanceHistoryTransactionType.Expense,
    },
    [BalanceHistoryType.BonusRefill]: {
        filterType: BalanceHistoryFilterType.Bonus,
        transactionType: BalanceHistoryTransactionType.Profit,
    },
};

export interface Balance {
    availableBalance: number;
    balance: number;
    bonusBalance: number;
    realBalance: number;
    reservedBalance: number;
    refundableBalance: number;
    heldRefundableBalance: boolean;
    heldBalance: number;
}

export interface BalanceStatistic {
    month: number;
    week: number;
    day: number;
}

export type BonusHistory = Array<{ sum: number; expirationDate: string }>;

interface BalanceHistoryItem {
    type: BalanceHistoryType;
    sum: number;
    dateEvent: string;
    files: Array<{ type: string; url: string }> | null;
}

export type BalanceHistory = Array<BalanceHistoryItem>;

// ----------------------- actions -------------------------------------
const SET_BALANCE = 'SET_BALANCE';
const SET_BALANCE_STATISTIC = 'SET_BALANCE_STATISTIC';
const SET_BONUS_HISTORY = 'SET_BONUS_HISTORY';
const SET_BALANCE_HISTORY = 'SET_BALANCE_HISTORY';

interface PayloadTypes {
    [SET_BALANCE]: Balance;
    [SET_BALANCE_STATISTIC]: BalanceStatistic;
    [SET_BONUS_HISTORY]: BonusHistory;
    [SET_BALANCE_HISTORY]: BalanceHistory;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setBalance = ActionCreator(SET_BALANCE);
export const setBalanceStatistic = ActionCreator(SET_BALANCE_STATISTIC);
export const setBalanceHistory = ActionCreator(SET_BALANCE_HISTORY);
export const setBonusHistory = ActionCreator(SET_BONUS_HISTORY);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface AccountBalance extends Balance {
    statistic: BalanceStatistic;
    bonusHistory: BonusHistory;
    balanceHistory: BalanceHistory;
}

export const ACCOUNT_STATISTICS_INITIAL_STATE: AccountBalance = {
    availableBalance: 0,
    balance: 0,
    realBalance: 0,
    bonusBalance: 0,
    reservedBalance: 0,
    refundableBalance: 0,
    heldRefundableBalance: false,
    heldBalance: 0,
    statistic: {
        day: 0,
        week: 0,
        month: 0,
    },
    bonusHistory: EMPTY_ARRAY,
    balanceHistory: EMPTY_ARRAY,
};

export default createReducer<AccountBalance, PayloadTypes>(ACCOUNT_STATISTICS_INITIAL_STATE, {
    [SET_BALANCE]: (state, { payload }) => ({
        ...state,
        ...payload,
    }),
    [SET_BALANCE_STATISTIC]: (state, { payload }) => ({
        ...state,
        statistic: payload,
    }),
    [SET_BALANCE_HISTORY]: (state, { payload }) => ({
        ...state,
        balanceHistory: payload,
    }),
    [SET_BONUS_HISTORY]: (state, { payload }) => ({
        ...state,
        bonusHistory: payload,
    }),
});
// ----------------------- /reducer --------------------------------------

// ----------------------- typeguards --------------------------------------
export const isHistoryOperationItem = (transaction: BalanceHistoryItem): boolean =>
    BALANCE_HISTORY_CONFIG[transaction.type]?.filterType === BalanceHistoryFilterType.Operation;

export const isHistoryBonusItem = (transaction: BalanceHistoryItem): boolean =>
    BALANCE_HISTORY_CONFIG[transaction.type]?.filterType === BalanceHistoryFilterType.Bonus;
// ----------------------- /typeguards --------------------------------------

// ----------------------- selectors ------------------------------------
export const selectBalance = (state: DefaultRootState): AccountBalance => state.balance;
export const selectAvailableBalance = (state: DefaultRootState): Balance['availableBalance'] =>
    selectBalance(state).availableBalance;
export const selectBalanceStatistic = (state: DefaultRootState): BalanceStatistic => selectBalance(state).statistic;
export const selectBalanceHistory = (state: DefaultRootState): BalanceHistory => selectBalance(state).balanceHistory;
export const selectBonusHistory = (state: DefaultRootState): BonusHistory => selectBalance(state).bonusHistory;
export const selectBalanceHistoryOperation = (state: DefaultRootState): Array<BalanceHistoryItem> =>
    selectBalanceHistory(state).filter(isHistoryOperationItem);
export const selectBalanceHistoryBonus = (state: DefaultRootState): Array<BalanceHistoryItem> =>
    selectBalanceHistory(state).filter(isHistoryBonusItem);
// ----------------------- /selectors ------------------------------------
